import {
    isSocialEnabled,
    isSocialEnabledOrAppEnabled,
} from '@socialFederation/helpers';
import { SuperbetRouteConfig } from '@superbet-group/web.types';

import {
    LegacyProduct,
    Product,
} from '@app/router/products/Product';
import { Link } from '@app/router/constants/Link';
import { RouteName } from '@app/router/constants/RouteName';
import { Title } from '@app/router/constants/Title';
import { addLoginRequirements } from '@app/router/utils';
import { AnalyticsScreenNames } from '@app/utils/analyticsScreenNames';
import SdsProfileContainer from '@modules/userProfile/account/SdsProfileContainer.vue';
import DocumentUploadContainer from '@modules/userProfile/documentUpload/DocumentUploadContainer.vue';
import IdCardVerificationUploadAdditional from '@accountFederation/modules/idCardVerification/IdCardVerificationUploadAdditional.vue';
import HistoryContainer from '@modules/userProfile/history/HistoryContainer.vue';
import ChatPlaceholderContainer from '@modules/userProfile/chat/ChatPlaceholderContainer.vue';
import { profilePage } from '@pages/ProfilePage';
import HelpAndSupportContainer from '@modules/userProfile/helpAndSupport/HelpAndSupportContainer.vue';
import SettingsBetslipPage from '@pages/SettingsBetslipPage/SettingsBetslipPage.vue';
import { getFederationComponent } from '@src/federations/getFederationComponent';
import NewAccountContainer from '@src/app/shared/components/Account/NewAccountContainer.vue';
import LicencePreferencesPage from '@accountFederation/pages/LicencePreferencesPage.vue';
import { loadAsyncWithLoadingState } from '@app/utils/loadAsyncComponent';
import { getEnabledRoutes } from '@app/router/routeHelpers';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import AccountSecurityContainer from '@accountFederation/modules/settings/AccountSecurityContainer.vue';

const myAccountClass = 'my-account';

const sharedMobileHeaderConfig = {
    isContracted: true,
};

const children: SuperbetRouteConfig[] = [
    {
        path: Link.Account.AccountSecurity,
        name: RouteName.Account.AccountSecurity,
        component: AccountSecurityContainer,
        meta: {
            title: Title.Account.AccountSecurity,
            headerTitle: Title.Account.AccountSecurity,
            layoutClass: myAccountClass,
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.Profile,
        name: RouteName.Account.Profile,
        component: SdsProfileContainer,
        meta: {
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profile,
            },
            headerTitle: 'headerTitle.profile',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.ReferAFriend,
        name: RouteName.Account.ReferAFriend,
        component: loadAsyncWithLoadingState<unknown>(() => import('@modules/userProfile/referAFriend/ReferAFriendContainer.vue')),
        meta: {
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileReferAFriend,
            },
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.MyMessages,
        name: RouteName.Account.MyMessages,
        isAccessible: Product.Account.MyMessages,
        component: loadAsyncWithLoadingState<unknown>(() => import('@modules/userProfile/inbox/InboxContainer.vue')),
        meta: {
            title: Title.Account.MyMessages,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileMessages,
            },
            headerTitle: 'bonus_comms.inbox.my_messages',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.History,
        name: RouteName.Account.History,
        component: HistoryContainer,
        meta: {
            title: Title.Account.History,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileHistory,
            },
            headerTitle: 'headerTitle.transactionHistory',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.Deposit,
        name: RouteName.Account.Deposit,
        component: loadAsyncWithLoadingState<unknown>(() => import('@walletFederation/src/pages/deposit/DepositPage.vue')),
        meta: {
            title: Title.Account.Deposit,
            layoutClass: `${myAccountClass} payment-page`,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileDeposit,
            },
            headerTitle: 'headerTitle.deposit',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.Withdrawal,
        name: RouteName.Account.Withdrawal,
        component: loadAsyncWithLoadingState<unknown>(() => import('@walletFederation/src/pages/withdrawal/WithdrawalPage.vue')),
        meta: {
            title: Title.Account.Withdrawal,
            layoutClass: `${myAccountClass} payment-page no-compact-betslip`,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileWithdrawal,
            },
            headerTitle: 'headerTitle.withdrawal',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.DocumentUpload,
        name: RouteName.Account.DocumentUpload,
        component: DocumentUploadContainer,
        meta: {
            title: Title.Account.DocumentUpload,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileDocumentUpload,
            },
            headerTitle: 'headerTitle.documentUpload',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.IdentityVerification,
        name: RouteName.Account.IdentityVerification,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/idCardVerification/IdCardVerificationPage.vue')),
        meta: {
            title: Title.Account.IdentityVerification,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileIdentityVerification,
            },
            headerTitle: 'headerTitle.identityVerification',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.IdentityVerificationUploadAdditional,
        name: RouteName.Account.IdentityVerificationUploadAdditional,
        component: IdCardVerificationUploadAdditional,
        meta: {
            title: Title.Account.IdentityVerificationUploadAdditional,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileDocumentUpload,
            },
            headerTitle: 'headerTitle.uploadAdditionalDocument',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.ResponsibleGaming,
        name: RouteName.Account.ResponsibleGaming,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/SdsResponsibleGamingContainer.vue')),
        props: true,
        meta: {
            headerTitle: 'headerTitle.responsibleGaming',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.LoginHistory,
        name: RouteName.Account.LoginHistory,
        isAccessible: Product.Account.LoginHistory,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/loginHistory/LoginHistory.vue')),
        meta: {
            headerTitle: 'headerTitle.loginHistory',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.DepositLimit,
        name: RouteName.Account.DepositLimit,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/DepositLimitContainer.vue')),
        meta: {
            headerTitle: 'deposit limit',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.BettingLimit,
        name: RouteName.Account.BettingLimit,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/BettingLimitContainer.vue')),
        meta: {
            headerTitle: 'total wager limit',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.SessionLimit,
        name: RouteName.Account.SessionLimit,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/SessionLimitContainer.vue')),
        meta: {
            headerTitle: 'session limit',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.LossLimit,
        name: RouteName.Account.LossLimit,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/LossLimitContainer.vue')),
        meta: {
            headerTitle: 'loss limit',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.TimeOut,
        name: RouteName.Account.TimeOut,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/TimeOutContainer.vue')),
        meta: {
            headerTitle: 'time out',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.AccountClosure,
        name: RouteName.Account.AccountClosure,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/AccountClosureContainer.vue')),
        meta: {
            headerTitle: 'account closure',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.SelfExclusion,
        name: RouteName.Account.SelfExclusion,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/SelfExclusionContainer.vue')),
        meta: {
            headerTitle: 'self exclusion',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.RealityCheck,
        name: RouteName.Account.RealityCheck,
        isAccessible: Product.Account.ResponsibleGaming,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/responsibleGaming/RealityCheckContainer.vue')),
        meta: {
            headerTitle: 'reality check',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },

    {
        path: Link.Account.ChangePhone,
        name: RouteName.Account.ChangePhone,
        isAccessible: Product.Account.ChangePhone,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/PhoneUpdateContainer.vue')),
        meta: {
            title: Title.Account.ChangePhone,
            headerTitle: 'pam.account.changePhoneNumber',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.MySocial,
        name: RouteName.Account.MySocial,
        isAccessible: isSocialEnabledOrAppEnabled(),
        component: getFederationComponent(() => import('social/SocialProfile')),
        meta: {
            title: Title.Account.MySocial,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.socialProfile,
            },
        },
    },
    {
        path: Link.Account.EditMySocial,
        name: RouteName.Account.EditMySocial,
        isAccessible: isSocialEnabledOrAppEnabled(),
        component: getFederationComponent(() => import('social/EditSocialProfilePage')),
        meta: {
            title: Title.Account.EditMySocial,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.editSocialProfile,
            },
            headerTitle: 'pam.accountMenu.editMySocial',
        },
    },
    {
        path: Link.Account.BlockedUsers,
        name: RouteName.Account.BlockedUsers,
        isAccessible: isSocialEnabled(),
        component: getFederationComponent(() => import('social/BlockedUsersList')),
        meta: {
            title: Title.Account.BlockedUsers,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.blockedUsers,
            },
            headerTitle: 'social.user_profile.settings.blocked_users.title',
        },
    },
    {
        path: Link.Account.ChangePassword,
        name: RouteName.Account.ChangePassword,
        isAccessible: Product.Account.ChangePassword,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/ChangePasswordContainer.vue')),
        meta: {
            title: Title.Account.ChangePassword,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.changePassword,
            },
            headerTitle: 'change password',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.BonusAndComms.MarketingConsent,
        name: RouteName.BonusAndComms.MarketingConsent,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/MarketingConsentContainer.vue')),
        meta: {
            title: Title.BonusAndComms.MarketingConsent,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.marketingConsent,
            },
            headerTitle: 'settings.privacyPolicy',
        },
    },
    {
        path: Link.Account.LicencePreferences,
        name: RouteName.Account.LicencePreferences,
        isAccessible: Product.Account.LicencePreferences,
        component: LicencePreferencesPage,
        meta: {
            title: Title.Account.LicencePreferences,
            layoutClass: myAccountClass,
            headerTitle: 'licence preferences',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
];

const publicChildren: SuperbetRouteConfig[] = [
    {
        path: Link.Account.SettingsBetslip,
        name: RouteName.Account.SettingsBetslip,
        component: SettingsBetslipPage,
        meta: {
            title: Title.Account.SettingsBetslip,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileSettings,
            },
            headerTitle: 'headerTitle.settingsBetslip',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.Settings,
        name: RouteName.Account.Settings,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/SettingsContainer.vue')),
        meta: {
            title: Title.Account.Settings,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.profileSettings,
            },
            headerTitle: 'headerTitle.settings',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.HelpAndSupport,
        name: RouteName.Account.HelpAndSupport,
        isAccessible: Product.Account.HelpAndSupport,
        component: HelpAndSupportContainer,
        meta: {
            title: Title.Account.HelpAndSupport,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.helpAndSupport,
            },
            headerTitle: 'pam.account.helpAndSupport',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.MyAccount,
        name: RouteName.Account.MyAccount,
        isAccessible: Product.Account.MyAccount,
        component: NewAccountContainer,
        meta: {
            title: Title.Account.MyAccount,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.myAccount,
            },
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.SwitchLanguage,
        name: RouteName.Account.SwitchLanguage,
        isAccessible: SiteLanguageConfig.hasLanguageSwitcher || Product.Account.SwitchLanguage,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/LanguageSwitcherContainer.vue')),
        props: true,
        meta: {
            headerTitle: 'switch language',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.SwitchTheme,
        name: RouteName.Account.SwitchTheme,
        component: loadAsyncWithLoadingState<unknown>(() => import('@accountFederation/modules/settings/ThemeSwitcherContainer.vue')),
        props: true,
        meta: {
            headerTitle: 'pam.account.theme',
            mobileHeaderConfig: sharedMobileHeaderConfig,
        },
    },
    {
        path: Link.Account.ChatPlaceholder,
        name: RouteName.Account.ChatPlaceholder,
        component: ChatPlaceholderContainer,
        meta: {
            title: Title.Account.ChatPlaceholder,
            layoutClass: myAccountClass,
            superAnalytics: {
                pageName: AnalyticsScreenNames.chat,
            },
            headerTitle: 'pam.accountMenu.chat',
        },
    },
];

const getChildren = () => {
    return children.map(addLoginRequirements);
};

export const accountRoutes: SuperbetRouteConfig[] = [
    {
        path: Link.Account.Container,
        name: RouteName.Account.Container,
        components: {
            ...profilePage,
        },
        // because Napoleon has getUserDetails on every refresh, while iCore targets don't, hence we need to have
        // a different logic for now.
        children: getEnabledRoutes(LegacyProduct, [...getChildren(), ...publicChildren]),
        meta: {
            title: Title.Account.Profile,
            hideMobileSubnavOnNewTopLevelNavigation: true,
        },
    },
];
